import React from 'react';
import { PlaceContext } from '@m4a3/api-client';

interface PlaceNotesProps {
  placeContext: PlaceContext | null;
  className?: string;
  isNarrow?: boolean;
  showManagementFeatures?: boolean;
  onAddNote?: () => void;
  isAddingNote?: boolean;
  noteText?: string;
  noteType?: string;
  onNoteTextChange?: (text: string) => void;
  onNoteTypeChange?: (type: string) => void;
  onSaveNote?: () => void;
  onCancelNote?: () => void;
  isSavingNote?: boolean;
  noteError?: string | null;
}

interface LocationNote {
  id: string;
  noteText: string;
  noteType: string;
  createdBy: string;
  createdAt: string;
}

const PlaceNotes = ({
  placeContext,
  className = '',
  isNarrow = false,
  showManagementFeatures = false,
  onAddNote,
  isAddingNote = false,
  noteText = '',
  noteType = 'general',
  onNoteTextChange,
  onNoteTypeChange,
  onSaveNote,
  onCancelNote,
  isSavingNote = false,
  noteError = null,
}: PlaceNotesProps) => {
  const notes = (placeContext?.notes && Array.isArray(placeContext.notes) ? placeContext.notes : []) as LocationNote[];
  
  // Don't show if no notes and no management features
  if (notes.length === 0 && !showManagementFeatures) {
    return null;
  }

  // Format note type for display
  const formatNoteType = (noteType: string) => {
    return noteType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get note type color
  const getNoteTypeColor = (noteType: string) => {
    switch (noteType) {
      case 'safety_consideration':
        return 'bg-red-100 text-red-800';
      case 'delivery_instructions':
        return 'bg-blue-100 text-blue-800';
      case 'access_information':
        return 'bg-green-100 text-green-800';
      case 'delivery_consideration':
        return 'bg-orange-100 text-orange-800';
      case 'general':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get note type emoji for compact view
  const getNoteTypeEmoji = (noteType: string) => {
    switch (noteType) {
      case 'safety_consideration':
        return '⚠️';
      case 'delivery_instructions':
        return '📋';
      case 'access_information':
        return '🔑';
      case 'delivery_consideration':
        return '📦';
      case 'general':
      default:
        return '📝';
    }
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg ${className}`}>
      {!isNarrow && (
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 flex items-center">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Notes ({notes.length})
          </h4>
          {showManagementFeatures && onAddNote && (
            <button
              onClick={onAddNote}
              disabled={isAddingNote}
              className="inline-flex items-center justify-center w-8 h-8 bg-black text-white rounded-full hover:bg-gray-800 focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Add Note"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
        </div>
      )}
      
      <div className="p-3">
        {/* Note Creation Form */}
        {isAddingNote && showManagementFeatures && (
          <div className="mb-4 pb-4 border-b border-gray-200" onClick={(e) => e.stopPropagation()}>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-medium text-green-900">Add Location Note</h5>
                <button
                  onClick={onCancelNote}
                  className="text-green-600 hover:text-green-800 text-xs"
                >
                  Cancel
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Note Type
                  </label>
                  <select
                    value={noteType}
                    onChange={(e) => {
                      e.stopPropagation();
                      onNoteTypeChange?.(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    style={{ position: 'relative', zIndex: 10 }}
                  >
                    <option value="general">General</option>
                    <option value="safety_consideration">Safety Consideration</option>
                    <option value="delivery_instructions">Delivery Instructions</option>
                    <option value="access_information">Access Information</option>
                    <option value="delivery_consideration">Delivery Consideration</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Note Text
                  </label>
                  <textarea
                    value={noteText}
                    onChange={(e) => onNoteTextChange?.(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    rows={3}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Enter note details..."
                  />
                </div>
                
                {noteError && (
                  <p className="text-xs text-red-600">{noteError}</p>
                )}
                
                <div className="flex gap-2">
                  <button
                    onClick={onSaveNote}
                    disabled={isSavingNote || !noteText.trim()}
                    className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingNote ? 'Saving...' : 'Save Note'}
                  </button>
                  <button
                    onClick={onCancelNote}
                    className="px-3 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="max-h-64 overflow-y-auto scrollbar-hide">
          {notes.length === 0 && !isAddingNote ? (
            <div className="text-center py-6 text-gray-500">
              <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm">No notes yet</p>
              {showManagementFeatures && (
                <p className="text-xs text-gray-400 mt-1">Click the + button above to add a note</p>
              )}
            </div>
          ) : notes.length > 0 && (
            <div className={`space-y-3 ${isNarrow ? 'space-y-2' : 'space-y-3'}`}>
              {notes.map((note) => (
                <div key={note.id} className={`border-l-4 border-blue-500 pl-3 py-2 ${isNarrow ? 'py-1' : 'py-2'}`}>
                  {isNarrow ? (
                    /* Compact layout: emoji + truncated text + hover */
                    <div className="flex items-start gap-2">
                      <span 
                        role="img" 
                        aria-label={formatNoteType(note.noteType)}
                        title={formatNoteType(note.noteType)}
                        className="text-sm flex-shrink-0 mt-0.5"
                      >
                        {getNoteTypeEmoji(note.noteType)}
                      </span>
                      <div 
                        className="flex-1 min-w-0 cursor-help"
                        title={`${formatNoteType(note.noteType)}: ${note.noteText} (by ${note.createdBy}, ${new Date(note.createdAt).toLocaleDateString()})`}
                      >
                        <p className="text-xs text-gray-800 break-words">{note.noteText}</p>
                      </div>
                    </div>
                  ) : (
                    /* Full layout: original detailed view */
                    <>
                      <div className="flex items-start justify-between mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getNoteTypeColor(note.noteType)}`}>
                          {formatNoteType(note.noteType)}
                        </span>
                        <div className="text-xs text-gray-500">
                          {new Date(note.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <p className="text-sm text-gray-800 mt-1">{note.noteText}</p>
                      <p className="text-xs text-gray-500 mt-1">by {note.createdBy}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaceNotes;
